import {Component} from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import {HoverInfo} from "./HoverInfo";
import { getDesignTypeConfiguration } from '../services/utils.service';
import FLoader from './FLoader';
import defaultImage from './../../src/images/defaultImage.svg';
import loaderImage from './../../src/images/loading.svg';
export const lazyImageSrcAttr = 'data-lazy-src';
export const lazyImageClassName = 'lazy-loader-image';

const MAX_CONCURRENT_IMAGES = 4;
let activeLoads = 0;
const queue = [];

function processQueue() {
  while (activeLoads < MAX_CONCURRENT_IMAGES && queue.length > 0) {
    const next = queue.shift();
    activeLoads++;
    next();
  }
}

/**
 * detect IE
 * returns version of IE or false, if browser is not Internet Explorer
 *
 * https://codepen.io/gapcode/pen/vEJNZN
 */
function detectIE() {
  var ua = window.navigator.userAgent;
  return (ua.indexOf('MSIE ') || ua.indexOf('Trident/') || ua.indexOf('Edge/')) > 0;
}

export function resetLazyImageQueue() {
  activeLoads = 0;
  queue.length = 0;
}

/**
 * Encapsule une image et affiche un spinner durant le chargement.
 * Par défaut, le src de l'image n'est pas renseigné. Il le sera par le composant ListView.
 *
 * Si besoin d'un chargement immédiat, il faut mettre la props 'lazy' à false.
 * Dans ce cas, le composant sert uniquement à afficher un spinner le temps du chargement de l'image.
 */
export class LazyImage extends Component {
  static propTypes = {
    lazy: PropTypes.bool,
    onLoad: PropTypes.func,
    onError: PropTypes.func,
    noCdn: PropTypes.bool,
    customStyle: PropTypes.bool,
    throttle: PropTypes.bool,
    priority: PropTypes.bool,
  };

  state = {
    loaded: false,
    hover:false,
    imageLoadError:false,
    shouldLoad: false,
    isVisible: false,
  };

  _isMounted = false;
  _isLoading = false;
  _intersectionObserver = null;
  _rootNode = null;

  componentDidMount() {
    this._isMounted = true;
    this._isLoading = false;
    if (this.props.lazy === false) {
      this.setState({ isVisible: true }, this.startImageLoad);
      return;
    }

    if (typeof window !== 'undefined' && 'IntersectionObserver' in window && this._rootNode) {
      this._intersectionObserver = new window.IntersectionObserver(
        entries => {
          const [entry] = entries;
          if (entry && (entry.isIntersecting || entry.intersectionRatio > 0)) {
            this.setState({ isVisible: true });
            this.disconnectObserver();
          }
        },
        { root: null, rootMargin: '250px', threshold: 0.01 }
      );
      this._intersectionObserver.observe(this._rootNode);
    } else {
      this.setState({ isVisible: true }, this.startImageLoad);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.disconnectObserver();
    // If component is unmounting while an image is still loading, decrement counter
    if (this._isLoading) {
      activeLoads = Math.max(0, activeLoads - 1);
      processQueue();
      this._isLoading = false;
    }
  }

  onImageLoaded = (event) => {
    if (!this._isMounted) {
      return;
    }
    this.setState({ loaded: true, imageLoadError: false });
    if (this._isLoading) {
      activeLoads = Math.max(0, activeLoads - 1);
      processQueue();
      this._isLoading = false;
    }
    if (this.props.onLoad) this.props.onLoad(event);
    if (event.target && detectIE()) {
      event.target.style.zoom = getComputedStyle(event.target).zoom || 1;
    }
  };

  onImageError = (event) => {
    if (!this._isMounted) {
      return;
    }
    this.setState({ loaded: true, imageLoadError:true });
    if (this._isLoading) {
      activeLoads = Math.max(0, activeLoads - 1);
      processQueue();
      this._isLoading = false;
    }
    if (this.props.onError) this.props.onError(event);
  };

  componentDidUpdate(prevProps, prevState) {
    const isLazy = this.props.lazy !== false;

    if (prevProps.src !== this.props.src) {
      this.setState({ loaded: false, imageLoadError: false, shouldLoad: false }, () => {
        const canStartNow = !isLazy || this.state.isVisible;
        if (canStartNow) {
          this.startImageLoad();
        }
      });
      return;
    }

    if (prevProps.lazy !== this.props.lazy && this.props.lazy === false) {
      this.disconnectObserver();
      if (!this.state.isVisible) {
        this.setState({ isVisible: true }, this.startImageLoad);
      } else {
        this.startImageLoad();
      }
      return;
    }

    if (isLazy && !prevState.isVisible && this.state.isVisible) {
      this.startImageLoad();
    }
  }

  disconnectObserver = () => {
    if (this._intersectionObserver) {
      this._intersectionObserver.disconnect();
      this._intersectionObserver = null;
    }
  };

  startImageLoad = () => {
    if (this.state.shouldLoad || this._isLoading) {
      return;
    }
    if (this.props.throttle) {
      this.enqueueImageLoad();
      return;
    }
    this.setState({ shouldLoad: true });
  };

  enqueueImageLoad = () => {
    const fn = () => {
      if (!this._isMounted) {
        activeLoads = Math.max(0, activeLoads - 1);
        processQueue();
        return;
      }
      // If we already started loading via other means (e.g. prop change), return the slot
      if (this.state.shouldLoad) {
        activeLoads = Math.max(0, activeLoads - 1);
        processQueue();
        return;
      }
      this._isLoading = true;
      this.setState({ shouldLoad: true });
    };
    if (this.props.priority) {
      // Find the last priority image in the queue
      let priorityIndex = 0;
      while (priorityIndex < queue.length && queue[priorityIndex].isPriority) {
        priorityIndex++;
      }
      fn.isPriority = true;
      queue.splice(priorityIndex, 0, fn); // Insert after last priority
    } else {
      queue.push(fn);
    }
    processQueue();
  };

  render() {
    const { imageLoadError, loaded, shouldLoad, isVisible } = this.state;
    let { src, className, classNameImg, lazy = true, lowResImage, hoverInfo, frame, fromFamily, bigImage, customStyle, throttle, priority, noCdn, onLoad, onError, ...others } = this.props;
    src = (src || '').replace('undefined', ''); // Bug IE < 11
    lowResImage = (lowResImage || '').replace('undefined', ''); // Bug IE < 11
    const srcHash = src.split("").reduce((a,b) => a + b.charCodeAt(0), 0);
    const designType = getDesignTypeConfiguration();
    const bigImageProp = this.props.bigImage || false;
    if (process.env.APP_ENV === 'production' && !this.props.noCdn){
        // TODO: consider dropping this pseudo cdn
        src = src.replace(/(https?):\/\/(?:api\.)?([^/]+\/?)/i, '$1://cdn' + Math.floor(srcHash % 10) + '.$2');
    }

    const canLoadImage = shouldLoad && (lazy ? isVisible : true);
    const srcAttr = canLoadImage ? { src } : {};
    const LowerResImage = lowResImage && <img decoding="async" src={lowResImage} className="LowResolution" alt="basse résolution"/>;
    
    const MainImage = (!imageLoadError && canLoadImage) ? (
      <img 
        decoding="async" 
        className={cn(lazyImageClassName, {loading: !loaded}, classNameImg)} 
        {...srcAttr} 
        {...others}
        // we used eager to avoid iframe lazy-loading issues
        loading="eager"
        onLoad={this.onImageLoaded} 
        onError={this.onImageError} 
        alt="contenu" 
        style={this.props.frame ? {transform: "scale(1) translate(0.2%, 1.5%)"} : {}}
      />
    ) : imageLoadError ? (
      <img src={defaultImage} alt="Image par défaut" />
    ) : null;

    const FigureContent = (
      <>
        {!loaded && !bigImageProp && this.props.fromFamily && <FLoader size="S" fspCatalogGranit="fspCatalogGranit" />}
        {!loaded && bigImageProp && this.props.fromFamily && <FLoader size="M"  />}
        {!hoverInfo && (!loaded && !this.props.fromFamily) && 
          <div className={`loaderdiv ${this.props.customStyle ? 'custom-loaderdiv' : ''}`}>
            <img src={loaderImage} className={`loader ${this.props.customStyle ? 'verticalCenter' : ''}`} alt="loader" />
          </div>
        }
        {LowerResImage}
        {MainImage}
      </>
    );

    if (hoverInfo) {
      return (
        <figure
          ref={node => {
            this._rootNode = node;
          }}
          className={cn(designType === "B" ? 'LazyImageB' : 'LazyImage', className, {loading: !loaded, hasLowResolution: lowResImage, hover: this.state.hover})}
          onMouseEnter={() => this.setState({hover: true})} 
          onMouseLeave={() => this.setState({hover: false})}
        >
          {this.state.hover && <HoverInfo hoverInfo={hoverInfo} />}
          {FigureContent}
        </figure>
      );
    }

    return (
      <figure
        ref={node => {
          this._rootNode = node;
        }}
        className={cn(designType === "B" ? 'LazyImageB' : 'LazyImage', className, {loading: !loaded, hasLowResolution: lowResImage})}
      >
        {FigureContent}
      </figure>
    );
  }
}